// src/utils/authStorage.js
// Centralized authentication storage utility
// Handles tokens, user data, and session management

/**
 * Authentication storage utility
 * - Tokens: sessionStorage (cleared on browser close)
 * - User data: localStorage (persists across sessions)
 * - Role/customer data: localStorage (persists across sessions)
 */
export const authStorage = {
    // Store token in sessionStorage
    setToken: (token) => {
        if (token) {
            sessionStorage.setItem("token", token);
            // Keep legacy localStorage token in sync to avoid stale-token reads.
            localStorage.setItem("token", token);
        }
    },
    // Retrieve token (prefer session token, fallback to legacy localStorage token)
    getToken: () => sessionStorage.getItem("token") || localStorage.getItem("token"),
    // Remove token from both storages
    removeToken: () => {
        sessionStorage.removeItem("token");
        localStorage.removeItem("token");
    },

    // Store user data in localStorage
    setUser: (user) => {
        if (user) {
            localStorage.setItem("user", JSON.stringify(user));
            localStorage.setItem("role", String(user.user_type));
            if (user.customer_id) {
                localStorage.setItem("customer_id", String(user.customer_id));
            }
        }
    },
    // Retrieve user data from localStorage
    getUser: () => {
        try {
            const userStr = localStorage.getItem("user");
            return userStr ? JSON.parse(userStr) : null;
        } catch (error) {
            console.warn("Failed to parse user data from localStorage:", error);
            return null;
        }
    },
    // Retrieve role from localStorage
    getRole: () => {
        const role = localStorage.getItem("role");
        return role ? Number(role) : null;
    },
    // Retrieve customer ID from localStorage
    getCustomerId: () => localStorage.getItem("customer_id"),

    // Store login status (session + persistent fallback for new tabs)
    setLoggedIn: (status) => {
        const value = String(status);
        sessionStorage.setItem("isLoggedIn", value);
        localStorage.setItem("isLoggedIn", value);
    },
    // Store login context (admin/pos/superadmin)
    setLoginContext: (context) => {
        if (context) {
            sessionStorage.setItem("loginContext", context);
        }
    },
    getLoginContext: () => sessionStorage.getItem("loginContext"),
    // Check login status (prefer session, fallback to local for new-tab continuity)
    isLoggedIn: () =>
        sessionStorage.getItem("isLoggedIn") === "true" ||
        localStorage.getItem("isLoggedIn") === "true",

    // Store customer data in localStorage (only for current user)
    setCustomerData: (customerId, customerData) => {
        if (customerId && customerData) {
            // Remove all customer_* entries before saving new one
            Object.keys(localStorage)
                .filter(key => key.startsWith('customer_'))
                .forEach(key => localStorage.removeItem(key));
            localStorage.setItem(`customer_${customerId}`, JSON.stringify(customerData));
        }
    },
    // Retrieve customer data from localStorage
    getCustomerData: (customerId) => {
        try {
            const data = localStorage.getItem(`customer_${customerId}`);
            return data ? JSON.parse(data) : null;
        } catch (error) {
            console.warn("Failed to parse customer data:", error);
            return null;
        }
    },

    // Store theme in localStorage
    setTheme: (themeId) => {
        if (themeId) {
            localStorage.setItem("theme", themeId);
        }
    },
    // Retrieve theme from localStorage
    getTheme: () => localStorage.getItem("theme"),
    // Remove theme from localStorage
    removeTheme: () => {
        localStorage.removeItem("theme");
        localStorage.removeItem("themeIndex"); // Legacy cleanup
    },

    // Clear session and persistent auth data
    clear: () => {
        // Clear session data
        sessionStorage.clear();
        // Clear auth-related persistent data
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        localStorage.removeItem("role");
        localStorage.removeItem("customer_id");
        localStorage.removeItem("isLoggedIn");
        // Keep theme so login pages can retain the last selected tenant style.
        // Theme can still be fully removed via clearAll() when needed.
        // Keep customer data for potential re-login
        // (Only clear specific customer data if needed)
    },
    // Clear all data including customer data
    clearAll: () => {
        sessionStorage.clear();
        localStorage.clear();
    }
};

// Check if user is authenticated
export const isAuthenticated = () => {
    return authStorage.isLoggedIn() && authStorage.getToken() && authStorage.getUser();
};

// Check if user has one of the allowed roles
export const hasRole = (allowedRoles) => {
    const user = authStorage.getUser();
    const userRole = user?.user_type ?? authStorage.getRole();
    if (userRole !== null && allowedRoles.includes(userRole)) return true;
    if (allowedRoles.includes(1)
        && user?.user_type === 2
        && String(user.role_name || "").toLowerCase() === "manager") {
        return true;
    }
    return false;
};

// Get current user object
export const getCurrentUser = () => {
    return authStorage.getUser();
};
